import { SocketEvents } from '@sea-snag/shared';
import { Alert, AlertDescription, Button, cn, Progress } from '@sea-snag/ui';
import {
  AlertCircle,
  Calendar,
  CalendarCheck,
  Check,
  Loader2,
  RefreshCw,
  Ship,
} from 'lucide-react';
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { useRetrySync } from '../../hooks/use-setup';
import { useSocketEvent } from '../../hooks/use-socket';
import {
  autoAdvances,
  canContinue,
  canRetry,
  failedPhases,
  initialSetupSyncState,
  retryFrom,
  type SetupPhase,
  setupSyncReducer,
  syncedCount,
} from '../../lib/setup-sync';
import type {
  FirmSyncCompletedPayload,
  FirmSyncFailedPayload,
  FirmSyncPhaseCompletedPayload,
  FirmSyncPhaseProgressPayload,
  FirmSyncPhaseStartedPayload,
} from '../../lib/socket-client';

const PHASE_CONFIG: { key: SetupPhase; label: string; icon: typeof Ship }[] = [
  { key: 'yachts', label: 'Yachts', icon: Ship },
  { key: 'availability', label: 'Availability', icon: Calendar },
  { key: 'reservations', label: 'Bookings', icon: CalendarCheck },
];

interface SetupProgressStepProps {
  orgPid: string;
  onComplete: () => void;
}

export function SetupProgressStep({ orgPid, onComplete }: SetupProgressStepProps) {
  const [state, dispatch] = useReducer(setupSyncReducer, initialSetupSyncState);
  const retrySync = useRetrySync(orgPid);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handlePhaseStarted = useCallback((data: FirmSyncPhaseStartedPayload) => {
    dispatch({ type: 'PHASE_STARTED', phase: data.phase, total: data.total });
  }, []);

  const handlePhaseProgress = useCallback((data: FirmSyncPhaseProgressPayload) => {
    dispatch({
      type: 'PHASE_PROGRESS',
      phase: data.phase,
      completed: data.completed,
      total: data.total,
    });
  }, []);

  const handlePhaseCompleted = useCallback((data: FirmSyncPhaseCompletedPayload) => {
    dispatch({ type: 'PHASE_COMPLETED', phase: data.phase, stats: data.stats });
  }, []);

  const handleSyncCompleted = useCallback((_data: FirmSyncCompletedPayload) => {
    dispatch({ type: 'SYNC_COMPLETED' });
  }, []);

  const handleSyncFailed = useCallback((data: FirmSyncFailedPayload) => {
    dispatch({
      type: 'SYNC_FAILED',
      phase: data.phase,
      error: data.error,
      retryable: data.retryable,
    });
  }, []);

  useSocketEvent(SocketEvents.FIRM_SYNC_PHASE_STARTED, handlePhaseStarted);
  useSocketEvent(SocketEvents.FIRM_SYNC_PHASE_PROGRESS, handlePhaseProgress);
  useSocketEvent(SocketEvents.FIRM_SYNC_PHASE_COMPLETED, handlePhaseCompleted);
  useSocketEvent(SocketEvents.FIRM_SYNC_COMPLETED, handleSyncCompleted);
  useSocketEvent(SocketEvents.FIRM_SYNC_FAILED, handleSyncFailed);

  // Only a clean run moves on by itself; after a failed phase the user retries or continues.
  const advances = autoAdvances(state);
  useEffect(() => {
    if (!advances) return;
    const timer = setTimeout(() => {
      onCompleteRef.current();
    }, 2000);
    return () => clearTimeout(timer);
  }, [advances]);

  const failed = failedPhases(state);
  const labelOf = (phase: SetupPhase) => PHASE_CONFIG.find((p) => p.key === phase)?.label ?? phase;

  function handleRetry() {
    const from = retryFrom(state);
    if (!from) return;
    retrySync.mutate(
      { fromPhase: from },
      { onSuccess: () => dispatch({ type: 'RETRY_STARTED', phase: from }) }
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <h3 className="text-lg font-semibold">
          {state.finished
            ? failed.length > 0
              ? 'Sync finished with an error'
              : 'Sync complete!'
            : state.yachts.status === 'failed'
              ? 'Sync failed'
              : 'Syncing your fleet...'}
        </h3>
        <p className="text-sm text-muted-foreground">
          {advances
            ? 'Your yachts, availability and bookings are ready.'
            : failed.length > 0 && state.finished
              ? 'Retry the part that failed, or continue and let the next scheduled sync pick it up.'
              : 'This may take a moment. Please keep this window open.'}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {PHASE_CONFIG.map(({ key, label, icon: Icon }) => {
          const phase = state[key];
          const pct = phase.total > 0 ? Math.round((phase.completed / phase.total) * 100) : 0;

          return (
            <div key={key} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon
                    className={cn(
                      'size-4',
                      phase.status === 'completed' && 'text-green-600',
                      phase.status === 'failed' && 'text-destructive',
                      phase.status === 'running' && 'text-primary',
                      phase.status === 'pending' && 'text-muted-foreground'
                    )}
                  />
                  <span className="text-sm font-medium">{label}</span>
                </div>

                <div className="flex items-center gap-1.5">
                  {phase.status === 'completed' && (
                    <>
                      <Check className="size-4 text-green-600" />
                      <span className="text-xs text-muted-foreground">
                        {syncedCount(phase)} items synced
                      </span>
                    </>
                  )}
                  {phase.status === 'running' && (
                    <>
                      <Loader2 className="size-4 animate-spin text-primary" />
                      {phase.total > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {phase.completed} / {phase.total}
                        </span>
                      )}
                    </>
                  )}
                  {phase.status === 'failed' && <AlertCircle className="size-4 text-destructive" />}
                </div>
              </div>

              <Progress
                value={phase.status === 'completed' ? 100 : pct}
                className={cn(
                  'h-2',
                  phase.status === 'completed' && '[&>[data-slot=progress-indicator]]:bg-green-600',
                  phase.status === 'failed' && '[&>[data-slot=progress-indicator]]:bg-destructive'
                )}
              />
            </div>
          );
        })}
      </div>

      {failed.map((phase) => (
        <Alert key={phase} variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {labelOf(phase)}: {state[phase].error || 'Sync failed. Please try again.'}
          </AlertDescription>
        </Alert>
      ))}

      {retrySync.isError && (
        <p className="text-center text-sm text-destructive">
          Couldn&apos;t start the retry: {retrySync.error.message}
        </p>
      )}

      {canRetry(state) && (
        <Button variant="outline" onClick={handleRetry} disabled={retrySync.isPending}>
          {retrySync.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="size-4" />
              Retry
            </>
          )}
        </Button>
      )}

      {canContinue(state) && (
        <Button variant="ghost" onClick={() => onCompleteRef.current()}>
          Continue anyway
        </Button>
      )}

      {advances && (
        <p className="text-center text-sm text-green-600 font-medium">
          Redirecting to your dashboard...
        </p>
      )}
    </div>
  );
}
